import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { notify } from '@/lib/notify';
import {
    FICHA_HELP_PATH,
    FICHA_JSON_EXAMPLE,
    FICHA_MD_TEMPLATE,
} from '../fichas/lib/productFichaTemplate';
import { TEMPLATE_HELP_PATH } from '../suppliers/templateManual';

async function copyText(label: string, text: string) {
    try {
        await navigator.clipboard.writeText(text);
        notify({ type: 'ok', text: `${label} copiado` });
    } catch {
        notify({ type: 'err', text: 'No se ha podido copiar' });
    }
}

const FIELDS: { name: string; meaning: string }[] = [
    { name: 'seo_slug', meaning: 'Clave para encontrar la ficha. Tiene que existir ya. No cambia la URL.' },
    { name: 'nuevo_seo_slug', meaning: 'Opcional. Nueva dirección, en minúsculas y guiones, distinta de seo_slug. La antigua redirige a la nueva. Vacío no cambia la URL.' },
    { name: 'nombre', meaning: 'Nombre visible. Sí se puede corregir por aquí.' },
    { name: 'seo_title', meaning: 'Título SEO. Unos 60 caracteres.' },
    { name: 'seo_description', meaning: 'Descripción SEO. Unos 155 caracteres.' },
    { name: 'descripcion_corta', meaning: 'Texto breve de la ficha.' },
    { name: 'descripcion_larga', meaning: 'Descripción. En el .md es la sección Descripción.' },
    { name: 'editorial', meaning: 'Criterio AiPeces. Se suma a la descripción, no la sustituye.' },
    { name: 'veredicto_aipeces', meaning: 'Para qué pesca sirve, en una o dos frases.' },
    { name: 'specs', meaning: 'Ficha técnica. En JSON, un objeto de pares clave y valor.' },
    { name: 'faq', meaning: 'Preguntas. En el .md, la sección FAQ.' },
    { name: 'pesca_tags', meaning: 'Etiquetas de pesca, separadas por comas.' },
    { name: 'imagen_alt', meaning: 'Texto alternativo de la imagen. No cambia la foto.' },
    { name: 'coleccion_slugs', meaning: 'Opcional. Hubs existentes, separados por comas, por ejemplo canas. Si no viene, las colecciones no cambian. Una colección que no existe rechaza la fila.' },
];

export function FichaHelpPage() {
    return (
        <div className="mx-auto max-w-3xl space-y-8">
            <div className="space-y-2">
                <h1 className="text-xl font-semibold">Instrucciones de las fichas</h1>
                <p className="text-sm text-muted-foreground">
                    Sirve para corregir textos y SEO de productos que ya existen, de muchos a la vez.
                    No crea productos y no cambia el precio. Esta página no está en la tienda pública ni en el menú.
                    La dirección es {FICHA_HELP_PATH}.
                </p>
            </div>

            <section className="space-y-2">
                <h2 className="text-sm font-semibold">No es la plantilla del proveedor</h2>
                <p className="text-sm text-muted-foreground">
                    El CSV del proveedor actualiza precio, stock, disponibilidad y fotos.
                    Esta vía actualiza el texto de una ficha ya creada.
                    Las instrucciones del CSV están en{' '}
                    <Link className="text-primary underline" to={TEMPLATE_HELP_PATH}>{TEMPLATE_HELP_PATH}</Link>.
                </p>
            </section>

            <section className="space-y-2">
                <h2 className="text-sm font-semibold">Orden</h2>
                <ol className="list-decimal space-y-1 pl-5 text-sm text-muted-foreground">
                    <li>Abrir Importar fichas, en /store/products/import.</li>
                    <li>Copiar el prompt y preparar un .md por producto, o un JSON con varios.</li>
                    <li>Subir el archivo o pegarlo. El máximo es 80 fichas.</li>
                    <li>Pulsar Dry-run. No guarda nada. Revisar slugs ausentes y campos vacíos.</li>
                    <li>Pulsar Aplicar a revisión. Las fichas quedan En revisión. No se publican.</li>
                    <li>En Productos, vista En revisión, elegir las filas y publicar o activar la venta.</li>
                </ol>
            </section>

            <section className="space-y-2">
                <h2 className="text-sm font-semibold">Qué no hace</h2>
                <p className="text-sm text-muted-foreground">
                    No cambia el precio, el stock ni las fotos.
                    La URL cambia solo si el archivo trae nuevo_seo_slug: la dirección antigua redirige a la nueva.
                    No crea la ficha: si el seo_slug no existe, esa fila sale como ausente.
                    No activa al proveedor y no lanza la sincronización del catálogo.
                </p>
            </section>

            <section className="space-y-2">
                <h2 className="text-sm font-semibold">Campos</h2>
                <div className="overflow-x-auto rounded-lg border border-border">
                    <table className="w-full text-sm">
                        <tbody>
                            {FIELDS.map((field) => (
                                <tr key={field.name} className="border-b border-border last:border-0">
                                    <th className="whitespace-nowrap px-3 py-2 text-left font-mono text-xs font-medium">{field.name}</th>
                                    <td className="px-3 py-2 text-muted-foreground">{field.meaning}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </section>

            <CopyBlock title="Ejemplo .md" text={FICHA_MD_TEMPLATE} />
            <CopyBlock title="Ejemplo JSON de varias fichas" text={FICHA_JSON_EXAMPLE} />

            <p className="text-sm text-muted-foreground">
                <Link className="text-primary underline" to="/store/products/import">Ir a importar fichas</Link>
            </p>
        </div>
    );
}

function CopyBlock({ title, text }: { title: string; text: string }) {
    return (
        <section className="space-y-2">
            <div className="flex items-center justify-between gap-2">
                <h2 className="text-sm font-semibold">{title}</h2>
                <Button type="button" variant="outline" size="sm" onClick={() => void copyText(title, text)}>
                    Copiar ejemplo
                </Button>
            </div>
            <pre className="overflow-x-auto rounded-lg border border-border bg-muted/30 p-3 text-xs">{text}</pre>
        </section>
    );
}
